// packages/shared-schema/src/point-card.ts
//
// Per `nfc-point-cards` — physical NFC/QR one-time point cards. A card
// carries a single random `code` (in the NFC/QR URL `?key=` AND typeable
// in the fan 功能頁). Redeeming credits the bound currency wallet and
// voids the code forever.
import { z } from 'zod';
/** Unambiguous alphabet — no 0/O/1/I/L to keep printed cards readable. */
export const POINT_CARD_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/** Generate a 16-char code grouped XXXX-XXXX-XXXX-XXXX (~78 bits). */
export function generatePointCardCode() {
    const n = 16;
    const out = [];
    const buf = new Uint8Array(n);
    (globalThis.crypto ?? globalThis.crypto).getRandomValues(buf);
    for (let i = 0; i < n; i++) {
        out.push(POINT_CARD_ALPHABET[buf[i] % POINT_CARD_ALPHABET.length]);
        if (i % 4 === 3 && i !== n - 1)
            out.push('-');
    }
    return out.join('');
}
/** Normalise user-typed input (strip spaces/dashes, uppercase) for lookup. */
export function normalizePointCardCode(input) {
    return input.replace(/[\s-]/g, '').toUpperCase();
}
export const PointCardCurrencyEnum = z.enum(['operator', 'platform']);
export const PointCardStatusEnum = z.enum(['active', 'redeemed', 'void']);
export const PointCardSchema = z.object({
    cardId: z.string().min(1),
    /** Normalised code (no dashes) — also the GSI key. */
    code: z.string().min(8).max(40),
    batchId: z.string().min(1),
    issuerType: z.enum(['operator', 'admin']),
    /** Bound operator (currency=operator). '' for platform-currency admin cards. */
    operatorId: z.string().max(128).default(''),
    currency: PointCardCurrencyEnum,
    faceValue: z.number().int().min(1).max(1_000_000),
    /** Character the fan lands on after redeem / on 2nd open. */
    characterId: z.string().max(64).default(''),
    /** 送角色卡 — redeeming ALSO grants ownership of `characterId`
     *  (skins row, same as a free acquire). Face value is still
     *  credited; already-owned fans just get the points. */
    grantCharacter: z.boolean().optional(),
    status: PointCardStatusEnum.default('active'),
    redeemedByFanId: z.string().optional(),
    redeemedAt: z.string().datetime().optional(),
    /** Denormalised batch name (required) + tag (optional) so the usage
     *  view shows them without joining the batch. */
    name: z.string().max(80).optional(),
    label: z.string().max(80).optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
});
export const PointCardBatchSchema = z.object({
    batchId: z.string().min(1),
    issuerType: z.enum(['operator', 'admin']),
    operatorId: z.string().max(128).default(''),
    currency: PointCardCurrencyEnum,
    faceValue: z.number().int().min(1).max(1_000_000),
    quantity: z.number().int().min(1).max(10_000),
    characterId: z.string().max(64).default(''),
    grantCharacter: z.boolean().optional(),
    /** Required human name for this issuance + optional tag. */
    name: z.string().max(80).optional(),
    label: z.string().max(80).optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
});
/** Create-batch request body (operator OR admin). */
export const PointCardBatchCreateBodySchema = z.object({
    /** Required human name for this issuance (e.g. "2026 跨年活動卡"). */
    name: z.string().min(1).max(80),
    currency: PointCardCurrencyEnum.default('operator'),
    faceValue: z.number().int().min(1).max(1_000_000),
    quantity: z.number().int().min(1).max(10_000),
    /** Operator to bind (operator-currency). For admin platform-currency
     *  cards this may be empty; for operator issuers the server forces it
     *  to the caller's own operatorId. */
    operatorId: z.string().max(128).optional(),
    characterId: z.string().max(64).optional(),
    /** 送角色卡: redeem grants ownership of `characterId` (required then). */
    grantCharacter: z.boolean().optional(),
    label: z.string().max(80).optional(),
}).refine((b) => !b.grantCharacter || !!b.characterId, {
    message: '送角色卡必須綁定角色',
    path: ['characterId'],
});
/** Redeem request body. `code` accepts dashed or plain form. */
export const PointCardRedeemBodySchema = z.object({
    code: z.string().min(8).max(40),
});
/** Admin cap edit (in 經營者審查). */
export const PointCardCapBodySchema = z.object({
    pointCardTotalCap: z.number().int().min(0).max(100_000_000),
    pointCardPerCardCap: z.number().int().min(0).max(1_000_000),
});
